import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  X, 
  FolderOpen, 
  ChevronRight, 
  Home, 
  Check,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { filesApi, DriveFolder } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

interface DriveConfigModalProps {
  isOpen: boolean;
  projectId: string;
  onClose: () => void;
}

type FolderType = 'team' | 'client';

export function DriveConfigModal({ isOpen, projectId, onClose }: DriveConfigModalProps) {
  const queryClient = useQueryClient();
  const [selectingFor, setSelectingFor] = useState<FolderType | null>(null);
  const [currentFolderId, setCurrentFolderId] = useState<string>('root');
  const [selectedTeamFolder, setSelectedTeamFolder] = useState<DriveFolder | null>(null);
  const [selectedClientFolder, setSelectedClientFolder] = useState<DriveFolder | null>(null);

  // Check drive availability
  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['files-status', projectId],
    queryFn: () => filesApi.getStatus(projectId),
    enabled: isOpen,
  });

  // Get current config
  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['files-config', projectId],
    queryFn: () => filesApi.getConfig(projectId),
    enabled: isOpen && status?.driveAvailable,
  });

  // Get folders for picker
  const { data: foldersData, isLoading: foldersLoading } = useQuery({
    queryKey: ['drive-folders', projectId, currentFolderId],
    queryFn: () => filesApi.listFolders(projectId, currentFolderId),
    enabled: isOpen && selectingFor !== null,
  });

  // Save config mutation
  const saveMutation = useMutation({
    mutationFn: (data: { teamFolderId?: string; clientUploadsFolderId?: string }) =>
      filesApi.updateConfig(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files-status', projectId] });
      queryClient.invalidateQueries({ queryKey: ['files-config', projectId] });
      queryClient.invalidateQueries({ queryKey: ['files', projectId] });
      onClose();
    },
  });

  // Initialize selected folders from config
  useEffect(() => {
    if (config) {
      if (config.teamFolderId && config.teamFolderName) {
        setSelectedTeamFolder({
          id: config.teamFolderId,
          name: config.teamFolderName,
          mimeType: 'application/vnd.google-apps.folder',
        });
      }
      if (config.clientUploadsFolderId && config.clientUploadsFolderName) {
        setSelectedClientFolder({
          id: config.clientUploadsFolderId,
          name: config.clientUploadsFolderName,
          mimeType: 'application/vnd.google-apps.folder',
        });
      }
    }
  }, [config]);

  // Reset folder navigation when opening picker
  useEffect(() => {
    if (selectingFor) {
      setCurrentFolderId('root');
    }
  }, [selectingFor]);

  const handleFolderClick = (folder: DriveFolder) => {
    setCurrentFolderId(folder.id);
  };

  const handleSelectFolder = (folder: DriveFolder) => {
    if (selectingFor === 'team') {
      setSelectedTeamFolder(folder);
    } else {
      setSelectedClientFolder(folder);
    }
    setSelectingFor(null);
  };

  const handleBreadcrumbClick = (folderId?: string) => {
    setCurrentFolderId(folderId || 'root');
  };

  const handleSave = () => {
    saveMutation.mutate({
      teamFolderId: selectedTeamFolder?.id,
      clientUploadsFolderId: selectedClientFolder?.id,
    });
  };

  if (!isOpen) return null;

  const folders = foldersData?.folders || [];
  const breadcrumb = foldersData?.breadcrumb || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-secondary/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-elevated max-w-lg w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-grey-100">
          <div className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-accent" />
            <h2 className="text-heading-3 text-secondary">
              {selectingFor 
                ? `Select ${selectingFor === 'team' ? 'Team' : 'Client Uploads'} Folder` 
                : 'Configure Google Drive'}
            </h2>
          </div>
          <button onClick={onClose} className="btn-icon">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {statusLoading || configLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
            </div>
          ) : !status?.driveAvailable ? (
            // Drive not available - redirect to Settings
            <div className="space-y-6">
              <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-secondary mb-1">Google Drive Not Connected</h3>
                  <p className="text-sm text-grey-600">
                    Google Drive needs to be configured in Settings before you can set up folder sharing.
                  </p>
                </div>
              </div>

              <p className="text-sm text-grey-600">
                Go to <strong>Settings → Integrations</strong> to connect your Google Drive service account.
              </p>
            </div>
          ) : selectingFor ? (
            // Folder picker view
            <div>
              {/* Breadcrumb */}
              <div className="flex items-center gap-1 mb-4 text-sm">
                <button
                  onClick={() => handleBreadcrumbClick(undefined)}
                  className="flex items-center gap-1 text-grey-500 hover:text-secondary transition-colors"
                >
                  <Home className="w-4 h-4" />
                  <span>My Drive</span>
                </button>
                {breadcrumb.map((folder, index) => (
                  <div key={folder.id} className="flex items-center gap-1">
                    <ChevronRight className="w-4 h-4 text-grey-400" />
                    <button
                      onClick={() => handleBreadcrumbClick(folder.id)}
                      className={cn(
                        "hover:text-secondary transition-colors",
                        index === breadcrumb.length - 1 
                          ? "text-secondary font-medium" 
                          : "text-grey-500"
                      )}
                    >
                      {folder.name}
                    </button>
                  </div>
                ))}
              </div>

              {/* Folders list */}
              {foldersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
                </div>
              ) : folders.length === 0 ? (
                <div className="py-8 text-center text-grey-500">
                  No folders here
                </div>
              ) : (
                <div className="space-y-1">
                  {folders.map((folder) => (
                    <div
                      key={folder.id}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-grey-50 transition-colors group"
                    >
                      <div
                        className="flex-1 flex items-center gap-3 cursor-pointer"
                        onClick={() => handleFolderClick(folder)}
                      >
                        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                          <FolderOpen className="w-5 h-5 text-accent" />
                        </div>
                        <span className="font-medium text-secondary">{folder.name}</span>
                        <ChevronRight className="w-4 h-4 text-grey-400 ml-auto" />
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSelectFolder(folder)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Select
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Select current folder button */}
              {currentFolderId !== 'root' && foldersData?.currentFolder && (
                <div className="mt-4 pt-4 border-t border-grey-100">
                  <Button
                    onClick={() => handleSelectFolder(foldersData.currentFolder!)}
                    className="w-full"
                  >
                    <Check className="w-4 h-4" />
                    Select "{foldersData.currentFolder.name}"
                  </Button>
                </div>
              )}
            </div>
          ) : (
            // Main config view
            <div className="space-y-6">
              <div className="p-4 bg-grey-50 rounded-lg">
                <p className="text-sm text-grey-600">
                  Link Google Drive folders to this project. Files from the team folder will be visible to clients, 
                  and clients can upload files to the uploads folder.
                </p>
              </div>

              {/* Team Folder */}
              <div>
                <label className="block text-sm font-medium text-secondary mb-2">
                  Team Folder (Shared with Client)
                </label>
                <p className="text-xs text-grey-500 mb-3">
                  Files from this folder will be visible to project members (read-only)
                </p>
                <div className="flex items-center gap-3">
                  {selectedTeamFolder ? (
                    <div className="flex-1 flex items-center gap-3 p-3 bg-grey-50 rounded-lg">
                      <FolderOpen className="w-5 h-5 text-accent" />
                      <span className="font-medium text-secondary">{selectedTeamFolder.name}</span>
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center gap-3 p-3 bg-grey-50 rounded-lg text-grey-500">
                      <FolderOpen className="w-5 h-5" />
                      <span>No folder selected</span>
                    </div>
                  )}
                  <Button variant="outline" onClick={() => setSelectingFor('team')}>
                    {selectedTeamFolder ? 'Change' : 'Select'}
                  </Button>
                </div>
              </div>

              {/* Client Uploads Folder */}
              <div>
                <label className="block text-sm font-medium text-secondary mb-2">
                  Client Uploads Folder
                </label>
                <p className="text-xs text-grey-500 mb-3">
                  Client uploads will be saved to this folder
                </p>
                <div className="flex items-center gap-3">
                  {selectedClientFolder ? (
                    <div className="flex-1 flex items-center gap-3 p-3 bg-grey-50 rounded-lg">
                      <FolderOpen className="w-5 h-5 text-success" />
                      <span className="font-medium text-secondary">{selectedClientFolder.name}</span>
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center gap-3 p-3 bg-grey-50 rounded-lg text-grey-500">
                      <FolderOpen className="w-5 h-5" />
                      <span>No folder selected</span>
                    </div>
                  )}
                  <Button variant="outline" onClick={() => setSelectingFor('client')}>
                    {selectedClientFolder ? 'Change' : 'Select'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-grey-50 border-t border-grey-100">
          {!status?.driveAvailable ? (
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          ) : selectingFor ? (
            <Button variant="ghost" onClick={() => setSelectingFor(null)}>
              Back
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave}
                isLoading={saveMutation.isPending}
                disabled={!selectedTeamFolder && !selectedClientFolder}
              >
                Save Configuration
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


